/**
 * RFQ Invoices Page
 * =================
 * Uses DataDashboard for a full-featured table view.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Layout from '../../components/layout/Layout';
import { DataDashboard, Column } from '../../shared/components';
import { useLang } from '../../shared/i18n';
import invoiceApi from '../../services/invoiceApi';
import AttachmentsModal from '../../components/common/AttachmentsModal';
import './RFQInvoices.scss';

const RFQInvoices: React.FC = () => {
    const { t } = useLang();
    const [data, setData] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 20,
        total: 0,
        pages: 1,
        has_next: false,
        has_prev: false
    });

    // Attachments Modal State
    const [isAttachmentsModalOpen, setIsAttachmentsModalOpen] = useState(false);
    const [selectedSupplierForAttachments, setSelectedSupplierForAttachments] = useState<{
        rfqId: string;
        supplierId: string;
        supplierName: string;
    } | null>(null);

    const fetchData = useCallback(async (params: any = {}) => {
        setLoading(true);
        setError(null);
        try {
            const page = params.page || 1;
            const limit = params.limit || 20;

            const response = await invoiceApi.getCompletedRFQs(page, limit);

            if (response.success && response.data) {
                setData(response.data.rfqs);
                setPagination({
                    page: response.data.page,
                    limit: response.data.page_size,
                    total: response.data.total,
                    pages: Math.ceil(response.data.total / response.data.page_size),
                    has_next: response.data.has_next,
                    has_prev: response.data.has_prev
                });
            } else {
                setError(response.message || 'Failed to fetch data');
            }
        } catch (err) {
            setError('Error connecting to server');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleDataFetch = (newParams: any) => {
        fetchData(newParams);
    };

    const handleOpenAttachments = (row: any) => {
        if (!row.selected_supplier) return;

        setSelectedSupplierForAttachments({
            rfqId: row.id,
            supplierId: row.selected_supplier,
            supplierName: row.selected_supplier_name || 'Supplier'
        });
        setIsAttachmentsModalOpen(true);
    };

    const handleCloseAttachments = () => {
        setIsAttachmentsModalOpen(false);
        setSelectedSupplierForAttachments(null);
    };

    // Column definitions
    const columns: Column[] = useMemo(() => [
        {
            key: 'company_name',
            title: t('colCompany'),
            type: 'text',
            sortable: true,
            width: '150px'
        },
        {
            key: 'product_name',
            title: t('colProduct'),
            type: 'text',
            sortable: true,
            width: '200px'
        },
        {
            key: 'selected_supplier_name',
            title: t('colSupplierRfq'),
            type: 'text',
            sortable: true,
            width: '150px'
        },
        {
            key: 'completed_at',
            title: t('colCompletionDate'),
            type: 'date',
            sortable: true,
            width: '120px'
        },
        {
            key: 'quantity',
            title: t('colQuantity'),
            type: 'number',
            sortable: true,
            width: '80px',
            render: (value: any, row: any) => `${value} ${row.unit || ''}`
        },
        {
            key: 'quotes',
            title: t('colQuotes'),
            type: 'action',
            sortable: false,
            width: '170px',
            render: (value: any, row: any) => {
                const count = row.attachment_count || 0;

                if (count === 0) {
                    return <span style={{ color: '#9ca3af', fontSize: '14px' }}>{t('noAttachments')}</span>;
                }

                return (
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            handleOpenAttachments(row);
                        }}
                        className="view-attachments-btn"
                        style={{
                            background: 'none',
                            border: 'none',
                            color: '#2563eb',
                            textDecoration: 'underline',
                            cursor: 'pointer',
                            padding: 0,
                            fontSize: '14px'
                        }}
                    >
                        {t('viewAttachments')} ({count})
                    </button>
                );
            }
        },
        {
            key: 'has_invoice',
            title: t('colInvoiceStatus'),
            type: 'status',
            sortable: true,
            width: '120px',
            render: (value: any) => {
                const status = value ? 'active' : 'pending';
                const label = value ? t('statusUploaded') : t('statusMissing');
                return <span className={`status-badge status-${status}`}>{label}</span>;
            }
        }
    ], [t]);

    const handleRowClick = (item: any) => {
        // Navigate or expand
    };

    return (
        <Layout
            pageTitle={t('rfqTitle')}
            pageDescription={t('rfqDescription')}
        >
            <DataDashboard
                title="RFQ Invoices"
                collection="rfq-invoices"
                data={data}
                columns={columns}
                onDataChange={setData}
                onRowClick={handleRowClick}
                showAddButton={false}
                showEditButton={false}
                showDeleteButton={false}
                showExportButton={false}
                isLoading={loading}
                error={error}
                paginationInfo={pagination}
                onDataFetch={handleDataFetch}
                noLayout={true}
            />

            {selectedSupplierForAttachments && (
                <AttachmentsModal
                    isOpen={isAttachmentsModalOpen}
                    onClose={handleCloseAttachments}
                    rfqId={selectedSupplierForAttachments.rfqId}
                    supplierId={selectedSupplierForAttachments.supplierId}
                    supplierName={selectedSupplierForAttachments.supplierName}
                />
            )}
        </Layout>
    );
};

export default RFQInvoices;
